/**
 * ThreadMessageCard — one message in an entry's thread, plus the recovery
 * actions its state allows.
 *
 * The meta line is the card's explanation of what actually happened, and it
 * never repeats the pill: the pill carries the state word, the meta line
 * carries the WHY (a held reason, a Gmail error, the probe count behind an
 * unknown delivery) and the WHEN. That is also why the pill is rendered
 * without `heldReason` here — the reason belongs to the sentence, not the tag.
 *
 * Three actions, gated by state and by outreach:write; every action sits in the bottom row:
 *   awaiting_approval | queued | held → Send now, bottom-left (never on a first-touch draft: the wizard sends those)
 *   held first touch, recent contact or a risky address → Send anyway in place of Send now (the address
 *                                      guard asks first: the confirm names the risk; discovr #742)
 *   failed | unknown_delivery         → Retry, bottom-left, the reason beside it
 *   unknown_delivery                 → Mark as sent (a reason is required: it
 *                                      is an operator asserting an outcome the
 *                                      system could not observe, so the audit
 *                                      trail has to say what they checked)
 *   queued | held                    → Stop (cancels the send), bottom-left right after the
 *                                      primary action, set apart by a wider gap and destructive
 *                                      styling. It used to sit far right as a misclick guard; the
 *                                      confirm dialog is now the only guard, so it must stay.
 *
 * "Reply" is deliberately absent — replies land in 2.4/2.5.
 */
import { useEffect, useId, useRef, useState } from 'react';
import type { OutreachMessage, OutreachMessageStatus } from '@discovr/types';
import { fmtDateTime } from '../../utils/campaignDetailHelpers';
import { zoneAbbrev } from '../../../lib/timeZones';
import { cn } from '../../../lib/utils';
import { isFirstTouchDraft } from './handoffStates';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { ActionConfirmDialog } from '../../../components/common/ActionConfirmDialog/ActionConfirmDialog';
import { OutreachMessageStatusPill, heldReasonLabel } from './OutreachMessageStatusPill';
import { RichBody } from './richBody';
import { ADDRESS_GUARD_ADMIN_ONLY_NOTE, addressGuardConfirm, addressGuardDraftNote, isAddressGuardReason } from './sendGuardCopy';

/** A cancel the system made at send time keeps its reason in lastError; the thread shows it as "Not sent". */
export const isNotSent = (m: OutreachMessage): boolean => m.direction === 'out' && m.status === 'cancelled' && !!m.lastError;

/** Stop, redraft, discard, reply and close cancel without a reason; the thread hides those unless asked. */
export const isHiddenCancelled = (m: OutreachMessage): boolean => m.direction === 'out' && m.status === 'cancelled' && !m.lastError;

// Exported so retryLabels.ts's retryTarget uses the exact same typed list
// (review HIGH: the two must stay identical, and a typed array catches a
// typo or a renamed OutreachMessageStatus member at compile time where a
// bare string[] membership test would not).
export const RECOVERABLE: OutreachMessageStatus[] = ['awaiting_approval', 'queued', 'held', 'failed', 'unknown_delivery'];
const CANCELLABLE: OutreachMessageStatus[] = ['queued', 'held'];
const BODY_CLAMP_CHARS = 600;

/** Inbound rich content is the creator's own email HTML; outbound is the
 *  composer's — never an outbound row's `bodyHtml`, which is the branded
 *  send envelope (unsubscribe link, postal address), not display content. */
function richBodySource(message: OutreachMessage): string | undefined {
  return message.direction === 'in' ? message.bodyHtml : message.bodyRichHtml;
}

function fromLabel(message: OutreachMessage, mailboxLabel?: string): string | undefined {
  if (mailboxLabel && message.fromAddress) return `${mailboxLabel} (${message.fromAddress})`;
  return mailboxLabel ?? message.fromAddress;
}

/** The "send anyway" a held first touch needs (Go only guards and cools down `initial`), or null when none applies. */
export function sendAnywayOverride(message: OutreachMessage): 'cooldown' | 'address_guard' | null {
  if (message.status !== 'held' || message.kind !== 'initial') return null;
  if (message.heldReason === 'recent_contact') return 'cooldown';
  return isAddressGuardReason(message.heldReason) ? 'address_guard' : null;
}

/** Only a future time is a schedule: a send-now carries its approval moment until the executor decides. */
export const isScheduledAhead = (message: OutreachMessage): message is OutreachMessage & { scheduledFor: string } =>
  !!message.scheduledFor && new Date(message.scheduledFor).getTime() > Date.now();

function scheduledLine(message: OutreachMessage & { scheduledFor: string }): string {
  const when = `Scheduled for ${fmtDateTime(message.scheduledFor)}`;
  if (!message.sendTz) return when;
  return `${when} · ${zoneAbbrev(message.sendTz, new Date(message.scheduledFor))}`;
}

/**
 * The WHY the header strip's sender+timestamp doesn't carry — null for the
 * plain cases (received/cancelled, or sent/queued with nothing extra to
 * say), since the bubble's own header strip (ThreadBubble, in ThreadView)
 * already says who/when for those. Held/failed/unknown-delivery keep their
 * reason; a still-queued scheduled send keeps its scheduled time + zone
 * (the strip's timestamp shows the same instant with no zone abbreviation).
 */
function messageStatusNote(message: OutreachMessage): string | null {
  if (isNotSent(message)) return `Not sent · ${message.lastError}`;
  switch (message.status) {
    case 'held':
      return message.heldReason ? `Held · ${heldReasonLabel(message.heldReason)}` : 'Held';
    case 'failed':
      return message.lastError ? `Failed · ${message.lastError}` : 'Failed';
    case 'unknown_delivery':
      return message.lastError ? `Unknown delivery · ${message.lastError}` : 'Unknown delivery';
    case 'queued':
      // Only a queued send can still be scheduled-for-later; awaiting_approval
      // and sending are both transient states past that point, so they fall
      // to the explicit null below rather than risk showing a stale
      // "Scheduled for <past time>" off a scheduledFor left over from queued.
      return isScheduledAhead(message) ? scheduledLine(message) : null;
    case 'sent':
    case 'received':
    case 'cancelled':
    case 'awaiting_approval':
    case 'sending':
      return null;
  }
}

function messageMetaLine(message: OutreachMessage, mailboxLabel?: string): string {
  const from = fromLabel(message, mailboxLabel);
  const parts: string[] = [];

  switch (message.status) {
    case 'sent':
      parts.push(`Sent ${fmtDateTime(message.sentAt ?? message.createdAt)}`);
      if (from) parts.push(`from ${from}`);
      break;
    case 'received':
      parts.push(`Received ${fmtDateTime(message.createdAt)}`);
      if (message.fromAddress) parts.push(`from ${message.fromAddress}`);
      break;
    case 'held':
      parts.push(message.heldReason ? `Held · ${heldReasonLabel(message.heldReason)}` : 'Held');
      if (from) parts.push(`from ${from}`);
      break;
    case 'failed':
      parts.push(message.lastError ? `Failed · ${message.lastError}` : 'Failed');
      break;
    case 'unknown_delivery':
      parts.push(message.lastError ? `Unknown delivery · ${message.lastError}` : 'Unknown delivery');
      break;
    case 'cancelled':
      parts.push(isNotSent(message) ? `Not sent · ${message.lastError}` : `Cancelled ${fmtDateTime(message.createdAt)}`);
      break;
    default:
      parts.push(isScheduledAhead(message) ? scheduledLine(message) : `Queued ${fmtDateTime(message.createdAt)}`);
      if (from) parts.push(`from ${from}`);
  }

  return parts.join(' · ');
}

export interface ThreadMessageCardProps {
  message: OutreachMessage;
  /** The pool mailbox's display name, when the pool read resolved this id. */
  mailboxLabel?: string;
  canWrite: boolean;
  /** Send anyway on a risky address is admin tier only (outreach:manage, owner ruling 2026-10-12). */
  canOverrideAddressGuard?: boolean;
  onResend: () => void;
  onSendAnyway?: () => void;
  onMarkSent: (reason: string) => void;
  onCancel: () => void;
  busy: boolean;
  /** This message's own mark-sent mutation state — distinct from `busy`,
   *  which also covers resend/cancel — so the "Mark as sent" panel can tell
   *  a settled failure from a settled success and only auto-close on the
   *  latter (a failure stays visible next to the error it caused). */
  markSentPending: boolean;
  markSentFailed: boolean;
  /** Hosted inside a ThreadBubble (Section D) — the bubble owns the frame, so
   *  the card's own border/background collapse to nothing. */
  inBubble?: boolean;
  /** This first-touch draft is approved and waits for an email: nothing is left to send by hand. */
  approvedWaiting?: boolean;
  /** A first mail to this creator was already sent, so a stopped first touch cannot come back as a draft (E9-165). */
  firstTouchSent?: boolean;
}

const STOP_RETURNS_DRAFT = 'The draft comes back: you can edit it or schedule it again.';
const STOP_RETURNS_DRAFT_HELD = 'Stop this mail? It comes back as a draft so you can edit it and send again.';
const STOP_CANCELS_HELD_NO_DRAFT = 'The held mail is cancelled. No draft comes back, because this creator already had a first mail.';
const STOP_CANCELS = 'The message leaves the thread. Sending it later means a fresh send.';

/** What Stop tells the admin: the BE writes the draft back for a queued or held first touch (E9-53, E9-62); a follow-up is just cancelled. */
export function stopDescription(message: Pick<OutreachMessage, 'kind' | 'status'>, firstTouchSent: boolean): string {
  const isFirstTouch = message.kind === 'initial';
  if (isFirstTouch && message.status === 'held') return firstTouchSent ? STOP_CANCELS_HELD_NO_DRAFT : STOP_RETURNS_DRAFT_HELD;
  if (isFirstTouch && message.status === 'queued') return STOP_RETURNS_DRAFT;
  return STOP_CANCELS;
}

export function ThreadMessageCard({
  message,
  mailboxLabel,
  canWrite,
  canOverrideAddressGuard = false,
  onResend,
  onSendAnyway,
  onMarkSent,
  onCancel,
  busy,
  markSentPending,
  markSentFailed,
  inBubble,
  approvedWaiting = false,
  firstTouchSent = false,
}: ThreadMessageCardProps) {
  const uid = useId();
  const [expanded, setExpanded] = useState(false);
  const [markingSent, setMarkingSent] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [confirmingSendAnyway, setConfirmingSendAnyway] = useState(false);
  const wasMarkSentPending = useRef(false);

  // Closes the panel only once the mutation actually settles, and only on
  // success — a failure leaves it open (with its reason still typed) next to
  // the alert ThreadView renders, instead of vanishing as if nothing happened.
  useEffect(() => {
    if (wasMarkSentPending.current && !markSentPending && !markSentFailed) {
      setMarkingSent(false);
      setReason('');
    }
    wasMarkSentPending.current = markSentPending;
  }, [markSentPending, markSentFailed]);

  // Rich content is never truncated — cutting raw HTML at a character offset
  // risks slicing inside a tag, so a rich body always renders in full.
  const richHtml = richBodySource(message);
  const isLong = !richHtml && message.bodyText.length > BODY_CLAMP_CHARS;
  const body = isLong && !expanded ? `${message.bodyText.slice(0, BODY_CLAMP_CHARS)}…` : message.bodyText;

  const override = sendAnywayOverride(message);
  // An address override names the address it is for, so without one there is nothing to confirm.
  const canSendAnyway = canWrite && override !== null && !!onSendAnyway && (override !== 'address_guard' || (canOverrideAddressGuard && !!message.toAddress));
  const guardConfirm = override === 'address_guard' ? addressGuardConfirm(message.heldReason ?? '', { to: message.toAddress }) : null;
  const draftGuard = isFirstTouchDraft(message.kind, message.status) ? message.addressGuard : undefined;
  // A risky-address hold only ever leaves through Send anyway; Send now would just be refused.
  const canResend = canWrite && RECOVERABLE.includes(message.status) && !isFirstTouchDraft(message.kind, message.status) && !canSendAnyway && override !== 'address_guard';
  const adminOnlyGuard = canWrite && override === 'address_guard' && !canOverrideAddressGuard;
  const canMarkSent = canWrite && message.status === 'unknown_delivery';
  const canCancel = canWrite && CANCELLABLE.includes(message.status);

  const recoveryLabel = message.status === 'failed' || message.status === 'unknown_delivery' ? 'Retry' : 'Send now';

  const handleMarkSent = () => {
    const trimmed = reason.trim();
    if (!trimmed) return;
    onMarkSent(trimmed);
    // Closing happens in the effect above once the mutation settles
    // successfully — not here, so a failure doesn't look like it worked.
  };

  const note = inBubble ? messageStatusNote(message) : messageMetaLine(message, mailboxLabel);

  return (
    <article className={cn('flex flex-col gap-2', inBubble ? 'border-0 bg-transparent p-0' : 'rounded-xl border border-border bg-card p-3')}>
      {/* In a bubble the status is the head's tag (Figma). */}
      {!inBubble && (
        <div data-placement="top-right" className="flex justify-end">
          <OutreachMessageStatusPill status={message.status} title={message.lastError} />
        </div>
      )}

      {richHtml ? (
        <RichBody html={richHtml} className={cn(inBubble ? 'text-base/6' : 'text-body-sm-regular', 'text-foreground')} />
      ) : (
        <p className={cn(inBubble ? 'text-base/6' : 'text-body-sm-regular', 'whitespace-pre-wrap text-foreground')}>{body}</p>
      )}
      {isLong ? (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="w-fit text-caption-medium text-(--brand-500) hover:underline"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      ) : null}

      {!canResend && note ? <p className="text-caption-regular text-muted-foreground">{note}</p> : null}
      {canWrite && !approvedWaiting && isFirstTouchDraft(message.kind, message.status) ? (
        <p className="text-caption-regular text-muted-foreground">Send this first touch from the send wizard: click the creator&apos;s row.</p>
      ) : null}
      {adminOnlyGuard ? <p role="note" className="text-caption-regular text-muted-foreground">{ADDRESS_GUARD_ADMIN_ONLY_NOTE}</p> : null}
      {draftGuard ? (
        <p role="note" className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-caption-regular text-amber-900">
          {addressGuardDraftNote(draftGuard.code, { canSendAnyway: canOverrideAddressGuard })}
        </p>
      ) : null}
      {message.attempts > 1 ? (
        <p className="text-caption-regular text-muted-foreground">{message.attempts} attempts</p>
      ) : null}

      {canResend || canSendAnyway || canMarkSent || canCancel ? (
        <div data-placement="bottom-left" className="flex flex-wrap items-center gap-2 pt-1">
          {canSendAnyway ? (
            <Button
              type="button"
              size="sm"
              disabled={busy}
              title={guardConfirm ? undefined : 'Contacted from another campaign recently — this sends it anyway'}
              onClick={guardConfirm ? () => setConfirmingSendAnyway(true) : onSendAnyway}
            >
              Send anyway
            </Button>
          ) : null}
          {canResend ? (
            <Button type="button" variant="outline" size="sm" disabled={busy} onClick={onResend}>
              {recoveryLabel}
            </Button>
          ) : null}
          {canCancel ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="ml-3 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
              aria-label="Stop sending this message"
              disabled={busy}
              onClick={() => setConfirmingCancel(true)}
            >
              Stop
            </Button>
          ) : null}
          {canResend && note ? <span className="text-caption-regular text-muted-foreground">{note}</span> : null}
          {canMarkSent && !markingSent ? (
            <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => setMarkingSent(true)}>
              Mark as sent
            </Button>
          ) : null}
        </div>
      ) : null}

      {markingSent ? (
        <div className="flex flex-col gap-1.5 rounded-lg border border-border bg-background p-3">
          <Label htmlFor={`${uid}-reason`}>
            What did you check?<span className="text-destructive"> *</span>
          </Label>
          <Textarea
            id={`${uid}-reason`}
            value={reason}
            rows={3}
            disabled={busy}
            placeholder="e.g. Found it in the mailbox's Sent folder"
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => {
                setMarkingSent(false);
                setReason('');
              }}
            >
              Discard
            </Button>
            <Button type="button" size="sm" disabled={busy || !reason.trim()} onClick={handleMarkSent}>
              Confirm mark as sent
            </Button>
          </div>
        </div>
      ) : null}

      {guardConfirm && onSendAnyway ? (
        <ActionConfirmDialog
          open={confirmingSendAnyway && canSendAnyway}
          onOpenChange={setConfirmingSendAnyway}
          variant="change"
          title={guardConfirm.title}
          description={guardConfirm.description}
          confirmLabel={guardConfirm.confirmLabel}
          hideConfirmIcon
          busy={busy}
          onConfirm={() => {
            onSendAnyway();
            setConfirmingSendAnyway(false);
          }}
        />
      ) : null}

      <ActionConfirmDialog
        open={confirmingCancel}
        onOpenChange={setConfirmingCancel}
        variant="delete"
        title="Stop this send?"
        description={stopDescription(message, firstTouchSent)}
        confirmLabel="Stop send"
        busy={busy}
        onConfirm={() => {
          onCancel();
          setConfirmingCancel(false);
        }}
      />
    </article>
  );
}
